import { keepPreviousData, useQuery } from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import { ApiError } from "@/src/core/api/types";
import type {
  IPaymentResponse,
  IPaymentResult,
  IPaymentQuery,
  IWalletBalanceResponse,
} from "../types";

export const paymentKeys = {
  all: ["payment"] as const,
  minimum: (bookingId: string | number) =>
    [...paymentKeys.all, "minimum", String(bookingId)] as const,
  walletBalance: () => [...paymentKeys.all, "walletBalance"] as const,
  walletQuote: (
    bookingId: string | number,
    amount: number | null,
    balance: number,
  ) =>
    [
      ...paymentKeys.all,
      "walletQuote",
      String(bookingId),
      amount,
      balance,
    ] as const,
};

/** `url=true` asks Laravel for a gateway URL; false only prices the payment. */
const getPayment = async (
  bookingId: string | number,
  withUrl: boolean,
  query: IPaymentQuery = {},
) => {
  const { data } = await api.laravel.get<IPaymentResponse>(
    `/booking/${bookingId}/payment?url=${withUrl}&${querystring.stringify(query)}`,
  );
  if (!data.success) throw new ApiError(200, data.message);
  return data.data;
};

/** Raw calls (Laravel). Throw an ApiError on failure. */
export const paymentApi = {
  /** Starts the payment: a gateway URL, or `paid` when the wallet covered it. */
  async startPayment(
    bookingId: string | number,
    query: IPaymentQuery,
  ): Promise<IPaymentResult> {
    return getPayment(bookingId, true, query);
  },

  /** The smallest first payment Laravel accepts for a booking (`url=false`, no amount). */
  async getMinimumPayment(bookingId: string | number): Promise<number> {
    return (await getPayment(bookingId, false)).amount;
  },

  async getWalletQuote(
    bookingId: string | number,
    amount: number | null,
  ): Promise<IPaymentResult> {
    return getPayment(bookingId, false, { amount, use_wallet: 1 });
  },

  /** Best effort: 0 when the balance can't be read (a host account gets 401). */
  async getWalletBalance(): Promise<number> {
    try {
      const { data } = await api.laravel.get<IWalletBalanceResponse>(
        "/user/wallet/balance",
      );
      return data.success ? Number(data.data?.current_balance) || 0 : 0;
    } catch {
      return 0;
    }
  },
};

/** The smallest amount the guest may pay now. */
export const useMinimumPaymentQuery = (
  bookingId: string | number | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: paymentKeys.minimum(bookingId ?? "none"),
    queryFn: async () =>
      (await getPayment(bookingId as string | number, false)).amount,
    enabled: enabled && bookingId !== undefined,
  });

export const useWalletBalanceQuery = (enabled: boolean) =>
  useQuery({
    queryKey: paymentKeys.walletBalance(),
    queryFn: paymentApi.getWalletBalance,
    enabled,
  });

export const useWalletQuoteQuery = (
  bookingId: string | number | undefined,
  amount: number | null,
  balance: number,
  enabled: boolean,
) =>
  useQuery({
    queryKey: paymentKeys.walletQuote(bookingId ?? "none", amount, balance),
    queryFn: () =>
      paymentApi.getWalletQuote(bookingId as string | number, amount),
    enabled: enabled && bookingId !== undefined,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
